import {
  GetNextBuyflowStepResponse,
  GetNextBuyflowStepRequest,
} from '@/lib/TypesOpenApi';
import {fetchBuyflowData} from './fetchBuyflowData';
import {useQuery, useQueryClient} from '@tanstack/react-query';
import {useCallback} from 'react';
import {fetchNutritionLabels} from './fetchNutritionLabels';

const ONE_SECOND = 1000; // in ms

/**
 * Will make cached request to fetch buyflow step data
 * Unfortunatly current Buyflow API not RESTfull,
 * this means all byflow requests using POST for quering data.
 * As a result both browser and NextJS caching layers ignore them.
 * To cache results and dedublicate them we manually will use tanstack query caching layer.
 *
 * Request caching based on user address and full payload,
 * so in reality they not will repeat during flow.
 * But mount/unmount/redirects can cause dublicated requests,
 * this mechanism will fire only one request per payload,
 * to reduce load on back-end.
 */
export const useCachedFetchByflowStepData = () => {
  const queryClient = useQueryClient();
  return useCallback(
    async (globalAddressId: string, payload: GetNextBuyflowStepRequest) => {
      return await queryClient.fetchQuery<GetNextBuyflowStepResponse>({
        queryKey: ['buyflowStep', globalAddressId, payload],
        queryFn: async () => await fetchBuyflowData(globalAddressId, payload),
        retry: 1, // will retry just once
        retryDelay: 5 * ONE_SECOND,
      });
    },
    [queryClient],
  );
};

export const useFetchNutritionLabels = (
  globalAddressId: string,
  productCodes: string[],
) => {
  return useQuery({
    queryKey: ['nutritionLabels', globalAddressId, ...productCodes],
    queryFn: async () =>
      await fetchNutritionLabels(globalAddressId, {
        addressId: globalAddressId,
        productCodes,
      }),
    retry: 1, // will retry just once
    retryDelay: 5 * ONE_SECOND,
    // will keep cache for a hour
    gcTime: 60 * 60 * ONE_SECOND,
    staleTime: 60 * 60 * ONE_SECOND,
  });
};
